/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { DateTime } from 'luxon'

import { Alert, SimpleTable } from '@/components/ui'
import { useApi } from '@/hooks'
import LiveDuration from '@/insights/components/LiveDuration'

import { fetchDoorHistory } from './api'
import type { DoorEvent } from './types'

/** The door history covers a rolling window ending now. */
export const DOOR_HISTORY_HOURS = 24

const DATE_FORMAT = 'dd-MMM-yyyy hh:mm a'

const OPENED = 'Door Open Date/Time'
const CLOSED = 'Door Closed Date/Time'
const DURATION = 'Duration of Door Open'

/** Table rows, newest opening first; a door still open has a blank closed cell and a running duration. */
export const doorHistoryRows = (events: DoorEvent[]) =>
  events
    .map((event) => ({
      id: event.id,
      opened: new Date(event.openedDate),
      closed: event.closedDate ? new Date(event.closedDate) : null,
    }))
    .sort((a, b) => b.opened.getTime() - a.opened.getTime())
    .map(({ id, opened, closed }) => ({
      [OPENED]: { id: `opened-${id}`, date: opened, display: DateTime.fromJSDate(opened).toFormat(DATE_FORMAT) },
      [CLOSED]: closed
        ? { id: `closed-${id}`, date: closed, display: DateTime.fromJSDate(closed).toFormat(DATE_FORMAT) }
        : '',
      [DURATION]: {
        value: (closed ?? new Date()).getTime() - opened.getTime(),
        display: <LiveDuration raisedTime={opened} clearedTime={closed ?? undefined} className="block min-w-fit" />,
      },
    }))

/** Every opening of one door in the last 24 hours; each column header sorts. */
export const DoorHistoryTable = ({
  companyId,
  assetId,
  monitoringPointId,
}: {
  companyId: number
  assetId: number
  monitoringPointId: number
}) => {
  const { api } = useApi()
  const [events, setEvents] = useState<DoorEvent[]>()
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setEvents(undefined)
    setError('')
    fetchDoorHistory(api, companyId, assetId, monitoringPointId, DateTime.now().minus({ hours: DOOR_HISTORY_HOURS }))
      .then((data) => {
        if (!cancelled) setEvents(data)
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load the door history.')
      })
    return () => {
      cancelled = true
    }
  }, [api, companyId, assetId, monitoringPointId])

  if (error) return <Alert type="error" message={error} />
  if (!events) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-base-content/60">
        <span className="loading loading-spinner loading-sm" /> Loading door history…
      </div>
    )
  }
  if (events.length === 0) {
    return <Alert type="info" message={`The door has not opened in the last ${DOOR_HISTORY_HOURS} hours.`} />
  }
  return <SimpleTable headers={[OPENED, CLOSED, DURATION]} body={doorHistoryRows(events)} />
}
